import {
  NATIVE_TOKEN_SENTINEL_ADDRESS,
  type RecentSendAddress,
  type SendAsset,
} from "./types";

const ERC20_TRANSFER_SELECTOR = "0xa9059cbb";
const ADDRESS_RE = /^0x[a-fA-F0-9]{40}$/;
const RECENT_SEND_KEY_PREFIX = "nexus-cross:dapp-ui:send:recent:";
const MAX_RECENT_SEND_ADDRESSES = 10;

function trimTrailingZeros(value: string): string {
  let end = value.length;
  while (end > 0 && value.charCodeAt(end - 1) === 48) end--;
  return value.slice(0, end);
}

export function normalizeAddress(address: string): `0x${string}` {
  return address.toLowerCase() as `0x${string}`;
}

export function isAddress(value: string): value is `0x${string}` {
  return ADDRESS_RE.test(value);
}

export function isNativeSendAsset(token: SendAsset): boolean {
  return normalizeAddress(token.address) === NATIVE_TOKEN_SENTINEL_ADDRESS;
}

function getRecentSendStorageKey(walletAddress: string): string {
  return `${RECENT_SEND_KEY_PREFIX}${walletAddress.toLowerCase()}`;
}

export function getRecentSendAddresses(
  walletAddress: string,
): RecentSendAddress[] {
  if (typeof window === "undefined") return [];

  try {
    const raw = window.localStorage.getItem(getRecentSendStorageKey(walletAddress));
    if (!raw) return [];

    const parsed = JSON.parse(raw) as RecentSendAddress[];
    if (!Array.isArray(parsed)) return [];

    return parsed
      .filter((item): item is RecentSendAddress => {
        return (
          item !== null &&
          typeof item === "object" &&
          isAddress(item.address) &&
          typeof item.updatedAt === "number"
        );
      })
      .slice(0, MAX_RECENT_SEND_ADDRESSES);
  } catch {
    return [];
  }
}

export function saveRecentSendAddress(
  walletAddress: string,
  recipient: `0x${string}`,
): RecentSendAddress[] {
  if (typeof window === "undefined") return [];

  const normalized = normalizeAddress(recipient);
  const next = [
    { address: normalized, updatedAt: Date.now() },
    ...getRecentSendAddresses(walletAddress).filter(
      (item) => item.address !== normalized,
    ),
  ].slice(0, MAX_RECENT_SEND_ADDRESSES);

  try {
    window.localStorage.setItem(
      getRecentSendStorageKey(walletAddress),
      JSON.stringify(next),
    );
  } catch {
    return getRecentSendAddresses(walletAddress);
  }

  return next;
}

export function clearRecentSendAddresses(walletAddress: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(getRecentSendStorageKey(walletAddress));
  } catch {
    // Ignore storage failures so disconnect still proceeds.
  }
}

export function parseTokenAmount(
  input: string,
  decimals: number,
): { value: bigint } | { error: string } {
  const trimmed = input.trim();

  if (!trimmed) return { error: "Enter an amount." };
  if (!/^\d+(\.\d*)?$/.test(trimmed)) {
    return { error: "Enter a valid amount." };
  }

  const [wholePart = "0", fractionPart = ""] = trimmed.split(".");
  if (fractionPart.length > decimals) {
    return { error: `Max ${decimals} decimal places.` };
  }

  const whole = BigInt(wholePart || "0");
  const paddedFraction = fractionPart.padEnd(decimals, "0");
  const fraction = paddedFraction ? BigInt(paddedFraction) : 0n;
  const unit = 10n ** BigInt(decimals);
  const value = whole * unit + fraction;

  if (value <= 0n) return { error: "Amount must be greater than 0." };

  return { value };
}

export function formatRawTokenAmount(raw: string, decimals: number): string {
  const value = BigInt(raw || "0");
  const unit = 10n ** BigInt(decimals);
  const whole = value / unit;
  const fraction = value % unit;

  if (fraction === 0n) return whole.toString();

  const fractionText = trimTrailingZeros(
    fraction.toString().padStart(decimals, "0"),
  );

  return `${whole.toString()}.${fractionText}`;
}

export function encodeErc20Transfer(
  recipient: `0x${string}`,
  amount: bigint,
): `0x${string}` {
  const recipientWord = recipient
    .replace(/^0x/, "")
    .toLowerCase()
    .padStart(64, "0");
  const amountWord = amount.toString(16).padStart(64, "0");
  return `${ERC20_TRANSFER_SELECTOR}${recipientWord}${amountWord}` as `0x${string}`;
}
